/**
 * NFCログインモーダルコンポーネント
 * ガイドラインに従ったグラスモーフィズムモーダル実装
 * ステップ表示機能付きNFCログイン体験
 */

import React from 'react';
import {
  Modal,
  Stack,
  Center,
  Avatar,
  Text,
  Group,
  Badge,
  Box,
  Button,
  Loader,
  Progress,
  ThemeIcon,
  Stepper,
  Transition,
} from '@mantine/core';
import {
  IconUser,
  IconCheck,
  IconCreditCard,
  IconShieldCheck,
  IconLogin,
  IconX,
  IconWifi,
} from '@tabler/icons-react';
import type { Staff, NFCLoginStep, NFCLoginModalProps } from '../../types';

// NFCLoginStepの型定義をエクスポート（コンポーネントからの利用のため）
export type { NFCLoginStep };

export const NFCLoginModal: React.FC<NFCLoginModalProps> = ({
  opened,
  onClose,
  staff,
  isProcessing,
  loginSuccess: _loginSuccess,
  currentStep,
  error,
}) => {
  // 権限ラベルとカラーの定義
  const getRoleBadge = (role: string) => {
    const config = {
      admin: { color: 'red', label: '管理者' },
      manager: { color: 'blue', label: 'マネージャー' },
      staff: { color: 'gray', label: 'スタッフ' },
    };
    
    const roleConfig = config[role as keyof typeof config] || config.staff;
    
    return (
      <Badge color={roleConfig.color} variant="filled">
        {roleConfig.label}
      </Badge>
    );
  };

  // ステップ情報の定義
  const getStepInfo = () => {
    switch (currentStep) {
      case 'detecting':
        return {
          icon: IconCreditCard,
          color: 'blue',
          title: 'カード検出中',
          description: 'NFCカードをタッチしてください',
          progress: 25,
        };
      case 'authenticating':
        return {
          icon: IconShieldCheck,
          color: 'orange',
          title: '認証処理中',
          description: 'カード情報を検証しています...',
          progress: 75,
        };
      case 'success':
        return {
          icon: IconCheck,
          color: 'green',
          title: 'ログイン成功',
          description: 'ダッシュボードへ移動しています...',
          progress: 100,
        };
      case 'error':
        return {
          icon: IconX,
          color: 'red',
          title: 'エラーが発生しました',
          description: error || '認証に失敗しました',
          progress: 0,
        };
      default:
        return {
          icon: IconWifi,
          color: 'gray',
          title: '待機中',
          description: 'NFCカードをお待ちしています',
          progress: 0,
        };
    }
  };

  const stepInfo = getStepInfo();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!isProcessing && currentStep !== 'success') {
          onClose();
        }
      }}
      title="NFCカード認証"
      size="md"
      centered
      closeOnClickOutside={false}
      closeOnEscape={currentStep === 'error'}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 24,
      }}
    >
      <Transition
        mounted={opened}
        transition="fade"
        duration={400}
        timingFunction="ease"
      >
        {(styles) => (
          <Stack gap="lg" style={styles}>
        {/* プログレスバー */}
        <Box>
          <Progress 
            value={stepInfo.progress} 
            size="sm" 
            color={stepInfo.color}
            animated={currentStep === 'detecting' || currentStep === 'authenticating'}
            striped={currentStep === 'authenticating'}
          />
        </Box>

        {/* 横並びステップインジケーター */}
        <Stepper 
          active={getCurrentStepperStep(currentStep)} 
          size="sm"
          color={stepInfo.color}
          styles={{
            stepIcon: {
              transition: 'all 0.3s ease',
            },
            step: {
              transition: 'all 0.3s ease',
            },
            stepDescription: {
              fontSize: '0.75rem',
            },
            stepLabel: {
              fontSize: '0.875rem',
            },
          }}
        >
          <Stepper.Step
            label="カード検出"
            description={currentStep === 'detecting' ? '検出中...' : currentStep !== 'idle' ? '完了' : '待機中'}
            icon={<IconCreditCard size={16} />}
            loading={currentStep === 'detecting'}
            color={currentStep === 'detecting' ? 'blue' : 
                   ['authenticating', 'success'].includes(currentStep) ? 'green' : 'gray'}
          />
          <Stepper.Step
            label="認証処理"
            description={currentStep === 'authenticating' ? '認証中...' : 
                        currentStep === 'success' ? '完了' : '待機中'}
            icon={<IconShieldCheck size={16} />}
            loading={currentStep === 'authenticating'}
            color={currentStep === 'authenticating' ? 'orange' : 
                   currentStep === 'success' ? 'green' : 'gray'}
          />
          <Stepper.Step
            label="ログイン"
            description={currentStep === 'success' ? '成功' : 
                        currentStep === 'error' ? '失敗' : '待機中'}
            icon={currentStep === 'error' ? <IconX size={16} /> : <IconLogin size={16} />}
            color={currentStep === 'success' ? 'green' : 
                   currentStep === 'error' ? 'red' : 'gray'}
          />
        </Stepper>

        {/* メインステータス表示 */}
        <Transition
          mounted={true}
          transition="slide-up"
          duration={300}
          timingFunction="ease"
        >
          {(styles) => (
            <Box
              style={{
                ...styles,
                padding: '1.5rem',
                backgroundColor: currentStep === 'success' ? 'var(--mantine-color-green-0)' :
                                currentStep === 'error' ? 'var(--mantine-color-red-0)' :
                                currentStep === 'authenticating' ? 'var(--mantine-color-orange-0)' :
                                'var(--mantine-color-blue-0)',
                borderRadius: '12px',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                border: `2px solid var(--mantine-color-${stepInfo.color}-2)`,
              }}
            >
              <Stack gap="md" align="center">
                {/* アイコン表示 */}
                <ThemeIcon
                  size={60}
                  radius="xl"
                  color={stepInfo.color}
                  variant={currentStep === 'success' ? 'filled' : 'light'}
                  style={{
                    transform: currentStep === 'success' ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {currentStep === 'authenticating' ? (
                    <Loader size={24} color="white" />
                  ) : (
                    <stepInfo.icon size={24} />
                  )}
                </ThemeIcon>

                {/* ステータステキスト */}
                <Stack gap="xs" align="center">
                  <Text size="lg" fw={600} c={stepInfo.color}>
                    {stepInfo.title}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {stepInfo.description}
                  </Text>
                </Stack>
              </Stack>
            </Box>
          )}
        </Transition>

        {/* スタッフ情報（認証成功時のみ表示） */}
        {staff && currentStep === 'success' && (
          <Transition
            mounted={currentStep === 'success'}
            transition="fade"
            duration={500}
            timingFunction="ease"
          >
            {(styles) => (
              <Box style={styles}>
                <Stack gap="md">
                  {/* スタッフアバター */}
                  <Center>
                    <Avatar
                      src={staff.photo_url || staff.photo_path || null}
                      size={80}
                      radius="md"
                    >
                      <IconUser size={40} />
                    </Avatar>
                  </Center>

                  {/* スタッフ情報 */}
                  <Stack gap="xs" align="center">
                    <Text size="xl" fw={600}>
                      {staff.name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {staff.email}
                    </Text>
                    <Group gap="xs">
                      {getRoleBadge(staff.role)}
                      {staff.store && (
                        <Badge variant="light">{staff.store.name}</Badge>
                      )}
                    </Group>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Transition>
        )}

        {/* アクションボタン */}
        {currentStep === 'error' && (
          <Group justify="center" mt="md">
            <Button 
              variant="light" 
              color="gray"
              onClick={onClose}
            >
              閉じる
            </Button>
            <Button 
              leftSection={<IconCreditCard size={16} />}
              onClick={() => window.location.reload()}
            >
              再試行
            </Button>
          </Group>
        )}

        {currentStep === 'detecting' && (
          <Group justify="center" mt="md">
            <Button variant="light" onClick={onClose}>
              キャンセル
            </Button>
          </Group>
        )}
      </Stack>
        )}
      </Transition>
    </Modal>
  );
};

// Stepperのアクティブステップを取得するヘルパー関数
function getCurrentStepperStep(step: NFCLoginStep): number {
  switch (step) {
    case 'detecting': return 0;
    case 'authenticating': return 1;
    case 'success': return 2;
    case 'error': return 2; // エラー時も最後のステップをアクティブ
    default: return -1;
  }
}